import Dexie from "dexie";
import envOptions from "../helpers/Env";

import { NotAuthenticatedError } from "@core/errors/Errors";

import type { LocalModelVersion } from "./ModelVersion";
import type { Model } from "./Model";
import type { Workspace } from "./Workspace";

export interface LocalModelInfo extends Model {
  deleted?: number | null;
  isNew?: boolean;
  lastSynch?: string | null;
  author?: string;
  lastVersion?: Partial<LocalModelVersion>;
}

type LocalWorkspace = Workspace;

export interface LocalMedia {
  models: (string | number)[];
  uploaded: boolean;
  uuid: string;
  data: Blob;
}

export class DexieDatabase extends Dexie {
  models!: Dexie.Table<LocalModelInfo, string | number>;
  modelVersions!: Dexie.Table<LocalModelVersion, string | number>;
  workspaces!: Dexie.Table<LocalWorkspace, number>;
  media!: Dexie.Table<LocalMedia, string>;

  constructor(userId: number | string, options = {}) {
    super(
      `simcision-user-db${envOptions.isDev ? "-dev" : "-live"}-${userId}`,
      options
    );

    //
    // Define tables and indexes
    // (Here's where the implicit table props are dynamically created)
    //
    this.version(4).stores({
      models:
        "&id, workspace, folder, folderSlug, [workspace+folderSlug], [workspace+folder]",
      modelVersions: "&model",
      workspaces: "&id, slug, organizationSlug, [organizationSlug+slug]"
    });
    this.version(8).stores({
      media: "++, uuid, model, [model+uuid]"
    });
    this.version(9).stores({
      media: "++, uuid, *models"
    });
    this.version(11).stores({
      media: "++, uuid, *models"
    });
  }
}

/**
 * Returns a Dexie DB for the current user
 * @param {number | string} userId
 */
export function getUserDB(userId: number | string | undefined, options = {}) {
  if (userId == null) {
    throw new Error("user is null");
  }

  const dbInstance = new DexieDatabase(userId, options);

  return dbInstance;
}

class LegacyDB extends Dexie {
  "attach-seq-store"!: Dexie.Table;
  "attach-store"!: Dexie.Table;
  "by-sequence"!: Dexie.Table;
  "detect-blob-support"!: Dexie.Table;
  "document-store"!: Dexie.Table;
  "local-store"!: Dexie.Table;
  "meta-store"!: Dexie.Table;

  constructor() {
    super("_pouch_simcision_projects");

    this.version(0.5).stores({
      "attach-seq-store": "++,&digestSeq,seq",
      "attach-store": "digest",
      "by-sequence": "++,&_doc_id_rev",
      "detect-blob-support": "",
      "document-store": "id,deletedOrLocal",
      "local-store": "_id",
      "meta-store": "id"
    });
  }
}

const legacyDB = new LegacyDB();

async function getAllLegacyModels() {
  const documents = await legacyDB["document-store"].toArray();
  console.log("documents", documents);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const models: Record<string, any> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const modelData: Record<string, any> = {};

  const modelsPromises = documents.map(async (doc) => {
    const modelRow = await legacyDB["by-sequence"].get(doc.seq);
    if (modelRow) {
      models[doc.id] = modelRow.data;
    }

    if (!modelRow.data) {
      console.log("no data", doc.id, modelRow);
    }

    if (modelRow?._attachments?.encoded) {
      const digest = modelRow?._attachments?.encoded?.digest;
      const dataRow = await legacyDB["attach-store"].get(digest);
      if (dataRow && dataRow.body) {
        modelData[doc.id] = await blobToString(dataRow.body);
      }
    }
    return modelRow;
  });

  await Promise.all(modelsPromises);

  console.log("models", models);
  console.log("modelData", modelData);
  return { models, modelData };
}
console.log(legacyDB);

let transferring = false;
/**
 * Transfer the models from the old pouchDB database
 * to the new Dexie database.
 * Add in all the required data to sync with the server.
 */
export async function transferLegacyData(user: {
  id: string | number;
  workspace?: { id: number };
}) {
  if (transferring) {
    console.warn("already transferring");
    return;
  }
  transferring = true;

  if (!user.workspace) {
    console.debug("user", user);
    console.error("User does not have a workspace");
    throw new NotAuthenticatedError();
  }

  const userId = user.id;
  const workspaceId = user.workspace.id;

  const userDB = getUserDB(userId);

  // retrieve the workspace data from the local DB.
  const workspace = await userDB.workspaces.get(workspaceId);
  if (
    workspace == null ||
    workspace.folders == null ||
    workspace.folders.length == 0
  ) {
    console.warn("workspace is not ready, retrying in 20 seconds");
    setTimeout(() => {
      transferLegacyData(user);
    }, 20000);
    return;
  }

  const folderId = workspace.folders[0].id;

  const { models, modelData } = await getAllLegacyModels();

  const userModels = Object.values(models).filter((m) => m?.user === userId);
  for (const model of userModels) {
    console.log("transferring model", model);
    try {
      const addedId = await userDB.models.add({
        workspace: workspaceId,
        id: model.id,
        name: model.name,
        description: model.description,
        modified: model.modified,
        created: model.created,
        isSimulationGame: model.isSimulationGame,
        type: "workspace_model",
        lastSynch: model.synched,
        author: model.author,
        isNew: model.synched == null,
        permissions: {
          canDelete: model?._permissions?.canEdit ?? true,
          canDownload: model?._permissions?.canDownload ?? true,
          canFork: model?._permissions?.canFork ?? true,
          canLock: model?._permissions?.canEdit ?? true,
          canUpdate: model?._permissions?.canEdit ?? true
        },
        folder: folderId,
        shares: [],
        sharesCount: 0,
        publicationsCount: 0,
        publications: []
      });
      console.log("added model", addedId);
    } catch (error) {
      console.log("could not transfer model", model.id);
      console.error(error);
      continue;
    }

    const modelDataEntry = modelData[model.id];
    if (modelDataEntry) {
      await userDB.modelVersions.put({
        model: model.id,
        data: modelDataEntry,
        created: model.modified,
        id: null,
        lastSynch: model.synched,
        media: []
      });
    }

    await removeLegacyModel(model.id);
  }

  transferring = false;
}

async function removeLegacyModel(modelId: number | string) {
  const doc = await legacyDB["document-store"].get(`${modelId}`);
  if (!doc) return;

  const seq = doc.seq;
  if (seq != null) {
    const entry = await legacyDB["by-sequence"].get(seq);
    if (entry) {
      const dataDigest = entry?._attachments?.encoded?.digest;
      if (dataDigest) {
        await legacyDB["attach-store"].delete(dataDigest);
      }
      await legacyDB["by-sequence"].delete(seq);
    }
  }

  return await legacyDB["document-store"].delete(`${modelId}`);
}

/**
 * Get the content of a file als text
 * @param file the file to read
 * @returns {Promise<string>} file content
 */
function blobToString(file: File): Promise<string | null | ArrayBuffer> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("loadend", function () {
      // reader.result contains the contents of blob as text
      resolve(reader.result);
    });
    reader.readAsText(file);
  });
}
